import {
  BoundaryCreatorOutcome,
  type ClientDispatchedToolArgs,
  type ClientDispatchedToolResult,
  OPEN_BOUNDARY_CREATOR_TIMEOUT_MS
} from 'api-validations/aurora'
import { create } from 'zustand'

type BoundaryCreatorResult = ClientDispatchedToolResult<'OpenBoundaryCreator'>

const REPLY_HEADROOM_MS = 10_000

export type BoundarySourceKey = NonNullable<
  ClientDispatchedToolArgs<'OpenBoundaryCreator'>['source']
>

export type BoundaryAutogenRequest = {
  planningFieldId: string
  source: BoundarySourceKey | null
  settle: (result: BoundaryCreatorResult) => void
}

const useRequestStore = create<{ pending: BoundaryAutogenRequest | null }>(() => ({
  pending: null
}))

export const requestBoundaryAutogen = ({
  planningFieldId,
  source
}: {
  planningFieldId: string
  source: BoundarySourceKey | null
}): Promise<BoundaryCreatorResult> =>
  new Promise((resolve) => {
    useRequestStore.getState().pending?.settle({
      outcome: BoundaryCreatorOutcome.Failed,
      reason: 'A newer request replaced this one before the outline was made.'
    })

    let settled = false
    const settle = (result: BoundaryCreatorResult) => {
      if (settled) return
      settled = true
      clearTimeout(expiry)
      takeBoundaryAutogen(request)
      resolve(result)
    }
    const request: BoundaryAutogenRequest = { planningFieldId, source, settle }
    const expiry = setTimeout(
      () =>
        settle({
          outcome: BoundaryCreatorOutcome.Failed,
          reason: 'The boundary editor did not finish the outline in time.'
        }),
      OPEN_BOUNDARY_CREATOR_TIMEOUT_MS - REPLY_HEADROOM_MS
    )

    useRequestStore.setState({ pending: request })
  })

export const usePendingBoundaryAutogen = (planningFieldId: string) =>
  useRequestStore((state) =>
    state.pending?.planningFieldId === planningFieldId ? state.pending : null
  )

export const takeBoundaryAutogen = (request: BoundaryAutogenRequest): boolean => {
  if (useRequestStore.getState().pending !== request) return false
  useRequestStore.setState({ pending: null })
  return true
}
